import { useMemo } from 'react'

import type { IPatientRoom } from 'features/Home/interfaces/IInfoPatient'
import { useFetchRoomDaysQuery } from 'features/Schedule/api/schedule.api'
import type { DaySummaryItem } from 'features/Schedule/api/schedule.types'

import type { DayResults } from './ehrAppointmentV2.types'
import { filterByRoom, roomBookingsFor, toRoomDay } from './roomFilter'

type Args = {
  siteId?: string
  results: DayResults[]
  previewDay: string
  room?: IPatientRoom
  editedId?: number
  enabled: boolean
  // Changes on every Find available, so bookings made since the last search are seen.
  searchId: number
}

type RoomFilter = {
  isActive: boolean
  roomName: string | null
  results: DayResults[]
  isFetching: boolean
  hasError: boolean
  // undefined: not filtering; null: the room's day is still loading.
  previewBookings?: DaySummaryItem[] | null
}

// Narrows EMR availability to one room using the day schedules; the waiting room is never filtered.
export const useRoomFilter = ({
  siteId,
  results,
  previewDay,
  room,
  editedId,
  enabled,
  searchId,
}: Args): RoomFilter => {
  const roomCode = room && !room.is_waiting_room ? String(room.value) : null
  const isActive = Boolean(enabled && siteId && roomCode)

  const dates = useMemo(
    () => [...new Set([...results.map((day) => day.day), previewDay])].sort(),
    [results, previewDay],
  )

  const { currentData, isFetching, isError } = useFetchRoomDaysQuery(
    { siteId: siteId ?? '', dates, searchId },
    { skip: !isActive, refetchOnMountOrArgChange: true },
  )

  const roomDays = useMemo(
    () => (currentData ?? []).map(({ date, payload }) => toRoomDay(date, payload)),
    [currentData],
  )

  // A failed room check falls back to the plain EMR list, which the hint then says.
  const isApplied = isActive && !isError && roomCode !== null

  const filtered = useMemo(
    () => (isApplied && roomCode ? filterByRoom(results, roomDays, roomCode, editedId) : results),
    [editedId, isApplied, results, roomCode, roomDays],
  )

  // A chosen room owns the preview from the start, so the whole day never flashes in first.
  const previewBookings = useMemo(() => {
    if (!siteId || !roomCode || isError) return undefined
    const day = roomDays.find((item) => item.date === previewDay)
    return day ? roomBookingsFor(day, roomCode, editedId) : null
  }, [editedId, isError, previewDay, roomCode, roomDays, siteId])

  return {
    isActive,
    roomName: room ? room.short_name ?? room.label ?? null : null,
    results: filtered,
    isFetching: isActive && isFetching,
    hasError: isActive && isError,
    previewBookings,
  }
}
